import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  capitalize,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import {
  type RunTestByIdParams,
  type Test,
  TestRunStatus,
  TestRunStatusList,
  TestSourceList,
} from "../types.ts";
import { humanizeText } from "../utils/text.ts";
import { getRunStatusColor } from "../utils.ts";
import { CreatedByColumn } from "./created-by-column.tsx";

export interface TestActionColumnProps extends GridRenderCellParams<Test> {
  onRunTest?: (testId: string) => void;
  onDeleteTest?: (testId: string) => void;
}

const TestActionColumn = (params: TestActionColumnProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { mutate: runTest, isPending: isRunningTest } = useApiMutation<void, RunTestByIdParams>(
    "POST",
    `/tests/${params.row.id}/run`,
    {
      onSuccess: (resp) => {
        enqueueSnackbar(
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Test run started successfully!</Typography>
            <IconButton
              aria-label="View run"
              onClick={() => {
                nav(`/runs/${resp.run_id}`);
              }}
              size="small"
              color="inherit"
            >
              <OpenInNewIcon htmlColor="#ffffff" />
            </IconButton>
          </Stack>,
          {
            variant: "success",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          },
        );

        // If there's an external handler, call it
        if (params.onRunTest) {
          params.onRunTest(params.row.id);
        }
      },
    },
  );

  const { mutate: deleteTest, isPending: isDeletingTest } = useApiMutation<void, void>(
    "DELETE",
    `/tests/${params.row.id}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test deleted successfully", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });

        // If there's an external handler, call it
        if (params.onDeleteTest) {
          params.onDeleteTest(params.row.id);
        }

        setDeleteConfirmOpen(false);
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting test: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        setDeleteConfirmOpen(false);
      },
    },
  );

  const handleDeleteConfirm = () => {
    deleteTest({});
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <Box alignItems="center" display="flex">
        <Tooltip title="View details">
          <IconButton
            component={Link}
            to={`/tests-main/tests/${params.row.id}`}
            size="small"
            color="primary"
            aria-label="details"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Run test">
          <IconButton
            size="small"
            color="default"
            disabled={isRunningTest}
            onClick={() => {
              runTest({});
            }}
            aria-label="Run test"
          >
            <PlayCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete test">
          <IconButton
            size="small"
            color="error"
            disabled={isDeletingTest}
            onClick={() => setDeleteConfirmOpen(true)}
            aria-label="Delete test"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Test Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the test "{params.row.name}"? This action will also
            delete all related test contexts and test runs.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={isDeletingTest}>
            {isDeletingTest ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const testColumns: GridColDef<Test>[] = [
  {
    field: "name",
    headerName: "Name",
    type: "string",
    minWidth: 260,
    flex: 1,
    editable: true,
    sortable: true,
  },
  {
    field: "created_at",
    type: "dateTime",
    headerName: "Created At",
    valueGetter: (val: Test["created_at"]) => {
      return new Date(val);
    },
    maxWidth: 180,
    flex: 1,
    sortable: true,
  },
  {
    field: "created_by",
    type: "string",
    headerName: "Created By",
    width: 120,
    renderCell: CreatedByColumn,
    sortable: false,
  },
  {
    field: "source",
    headerName: "Source",
    type: "singleSelect",
    valueOptions: TestSourceList,
    editable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<Test>) => {
      return (
        <Chip label={humanizeText(params.value)} variant="outlined" size="small" color="primary" />
      );
    },
    maxWidth: 180,
    flex: 1,
  },
  {
    field: "run_status",
    type: "singleSelect",
    valueOptions: TestRunStatusList,
    headerName: "Run Status",
    renderCell: (params: GridRenderCellParams<Test>) => {
      if (!params.value) {
        return null;
      }

      return (
        <Chip
          icon={
            params.value === TestRunStatus.RUNNING ? (
              <CircularProgress size={12} color="inherit" />
            ) : undefined
          }
          label={capitalize(params.value.replace(/_/g, " "))}
          variant="outlined"
          size="small"
          color={getRunStatusColor(params.value as Test["run_status"])}
        />
      );
    },
    width: 140,
    sortable: true,
  },
  {
    field: "actions",
    type: "actions",
    headerName: "Actions",
    width: 150,
    renderCell: TestActionColumn,
    sortable: false,
  },
];
